import React from 'react';
import styles from '../styles/Live.module.css';
import { seriesColor } from '../lib/palette';

/**
 * LiveTimingTable
 *
 * Position / driver / gap / interval / last lap / best lap / tire / stint age.
 * Rows are clickable to toggle a driver in the telemetry panel.
 * Last-lap time colors: green = personal best, purple = session best.
 */
const TIRE_KEYS = {
  SOFT: styles.tireSOFT,
  MEDIUM: styles.tireMEDIUM,
  HARD: styles.tireHARD,
  INTERMEDIATE: styles.tireINTER,
  WET: styles.tireWET,
};

const TIRE_SHORT = {
  SOFT: 'S',
  MEDIUM: 'M',
  HARD: 'H',
  INTERMEDIATE: 'I',
  WET: 'W',
};

function Tire({ compound, age }) {
  if (!compound) {
    return <span className={`${styles.tireBadge} ${styles.tireUNKNOWN}`}>—</span>;
  }
  const up = String(compound).toUpperCase();
  const cls = TIRE_KEYS[up] || styles.tireUNKNOWN;
  const short = TIRE_SHORT[up] || up[0];
  return (
    <span className={`${styles.tireBadge} ${cls}`}>
      {short}
      {age != null && <span className={styles.tireAge}>{age}</span>}
    </span>
  );
}

export default function LiveTimingTable({ drivers, selected, onToggle, onHover }) {
  if (!drivers || drivers.length === 0) {
    return <div style={{ fontFamily: "var(--mono)", fontSize: '0.65rem', color: '#8c8c8c', padding: 8 }}>waiting for timing data…</div>;
  }

  return (
    <table className={styles.timingTable}>
      <thead>
        <tr>
          <th>P</th>
          <th>Drv</th>
          <th>Gap</th>
          <th>Int</th>
          <th>Last</th>
          <th>Best</th>
          <th>Tire</th>
        </tr>
      </thead>
      <tbody>
        {drivers.map((d, i) => {
          const isSel = selected?.includes(d.number);
          const lastCls = d.last_lap_overall_best
            ? styles.overallBest
            : d.last_lap_personal_best
            ? styles.personalBest
            : '';
          return (
            <tr
              key={d.number}
              className={`${styles.timingRow} ${isSel ? styles.timingRowSelected : ''}`}
              onClick={() => onToggle?.(d.number)}
              onPointerEnter={() => onHover?.(d.number)}
              onPointerLeave={() => onHover?.(null)}
            >
              <td className={styles.posCell}>{d.position ?? '—'}</td>
              <td className={styles.tlaCell}>
                <span className={styles.teamStripe} style={{ background: seriesColor(d, i) }} />
                {d.tla || d.number}
              </td>
              <td className={styles.gapCell}>
                {d.retired ? <span className={styles.statusOut}>OUT</span>
                  : d.in_pit ? <span className={styles.statusIn}>PIT</span>
                  : d.gap_leader || '—'}
              </td>
              <td className={styles.gapCell}>{d.interval || '—'}</td>
              <td className={`${styles.lapTimeCell} ${lastCls}`}>{d.last_lap || '—'}</td>
              <td className={styles.lapTimeCell}>{d.best_lap || '—'}</td>
              <td className={styles.tireCell}>
                <Tire compound={d.tire_compound} age={d.tire_age} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
